import type { ImageContentFit } from "expo-image";
/* ----------------- UI Components --------------- */
import { useMemo } from "react";
import { ScrollView, type ViewStyle, useWindowDimensions } from "react-native";
import { useDevice } from "../../useDevice";
import { ScrollButtonFlatlist } from "../ChatUI/ScrollButtonFlatlist";
import { ZoomImage } from "../MarkdownRenderer/ZoomImage";
import { calculateCardsToShow } from "./helpers";

export type ImageType = {
	id: string;
	url: string;
	name?: string;
	caption?: string;
};

interface Props {
	images: ImageType[];
	containerStyle?: ViewStyle;
	useResponsiveImageWidthWeb?: boolean;
	contentFit?: ImageContentFit;
	customImageHeight?: number;
	customImageWidth?: number;
	maxSingleImageHeight?: number;
	maxSingleImageContainerWidth?: number;
}

const SIDE_PADDING = 16;

const ImagesCarousel = ({
	images,
	containerStyle,
	useResponsiveImageWidthWeb,
	contentFit,
	customImageHeight,
	customImageWidth,
	maxSingleImageHeight = 320,
	maxSingleImageContainerWidth = 420,
}: Props) => {
	const { width: windowWidth } = useWindowDimensions();
	const { isMobile } = useDevice();

	const isSingle = images.length === 1;
	const containerWidth = Math.min(
		windowWidth - SIDE_PADDING * 2,
		maxSingleImageContainerWidth,
	);

	// Multi-image tile sizing
	const imageWidth = useMemo(() => {
		const cardsToShow = calculateCardsToShow(windowWidth);
		return (windowWidth - SIDE_PADDING * (cardsToShow + 1)) / cardsToShow;
	}, [windowWidth]);

	const imageHeight = isMobile ? 200 : customImageHeight || 300;

	const buildZoomProps = (index: number) =>
		isSingle
			? {
					autoSize: true as const,
					containerWidth,
					maxHeight: maxSingleImageHeight,
					contentFit: contentFit ?? "contain",
					initialIndex: 0,
				}
			: {
					width:
						isMobile || useResponsiveImageWidthWeb
							? imageWidth
							: customImageWidth || 300,
					height: imageHeight,
					contentFit: contentFit ?? "cover",
					initialIndex: index,
				};

	if (isMobile) {
		return (
			<ScrollView
				horizontal
				showsHorizontalScrollIndicator
				contentContainerStyle={{
					paddingHorizontal: SIDE_PADDING,
					paddingBottom: 8,
					gap: isSingle ? 0 : 16,
					justifyContent: "center",
					alignItems: "center",
					width: isSingle || images.length <= 2 ? "100%" : "auto",
					...(containerStyle || {}),
				}}
			>
				{images.map((item, index) => (
					<ZoomImage
						key={item.id}
						url={item.url}
						name={item.name}
						allImages={images}
						borderRadius={12}
						{...buildZoomProps(index)}
					/>
				))}
			</ScrollView>
		);
	}

	return (
		<ScrollButtonFlatlist
			data={images}
			keyExtractor={(item) => item.id}
			renderItem={({ item, index }) => (
				<ZoomImage
					url={item.url}
					name={item.name}
					allImages={images}
					borderRadius={12}
					{...buildZoomProps(index)}
				/>
			)}
			horizontal
			showsHorizontalScrollIndicator
			contentContainerStyle={{
				paddingBottom: 8,
				// paddingHorizontal: SIDE_PADDING,
				gap: isSingle ? 0 : 16,
				justifyContent: "center",
				alignItems: "center",
				width: isSingle || images.length <= 2 ? "100%" : "auto",
				...(containerStyle || {}),
			}}
		/>
	);
};

export default ImagesCarousel;
